import React, { useState, useEffect, useCallback } from "react";
import styled from "styled-components";
import { Card, Button, Alert, Spin, List, Avatar, message } from "antd";
import {
  BankOutlined,
  PlusOutlined,
  CheckCircleOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import { usePlaidLink } from "react-plaid-link";
import { plaidApi } from "../../lib/api/paymentApi";
import { trackEvent } from "../../helpers/mixpanel";

const PlaidLink = ({ tenantId, onSuccess, onExit }) => {
  const [loading, setLoading] = useState(false);
  const [linkToken, setLinkToken] = useState(null);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [error, setError] = useState(null);

  const loadBankAccounts = useCallback(async () => {
    try {
      setLoading(true);
      const response = await plaidApi.getBankAccounts(tenantId);

      if (response.success) {
        // Backend returns { success, data: { bankAccounts: [...] } }
        // Normalise each account so the UI fields (name, mask) are always present
        const raw = response.data?.bankAccounts || response.data?.accounts || [];
        const normalised = raw.map((acct) => ({
          ...acct,
          name: acct.accountName || acct.name || "Bank Account",
          mask: acct.lastFourDigits || acct.mask || "••••",
        }));
        setBankAccounts(normalised);
      }
    } catch (err) {
      console.error("Error loading bank accounts:", err);
      setError("Failed to load bank accounts");
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  const createLinkToken = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await plaidApi.createLinkToken(tenantId);

      if (response.success) {
        setLinkToken(response.data.linkToken);

        try {
          trackEvent("plaid_link_token_created", { tenantId });
        } catch (err) {
          console.warn("Failed to track event:", err);
        }
      }
    } catch (err) {
      setError("Failed to create link token. Please try again.");
      console.error("Error creating link token:", err);
    } finally {
      setLoading(false);
    }
  };

  const handlePlaidSuccess = async (publicToken, metadata) => {
    try {
      setLoading(true);

      const response = await plaidApi.exchangeToken(tenantId, publicToken);

      if (response.success) {
        message.success("Bank account linked successfully!");

        // Reload bank accounts
        await loadBankAccounts();

        // Call success callback
        onSuccess(publicToken);

        try {
          trackEvent("plaid_account_linked", {
            tenantId,
            institutionId: metadata.institution?.institution_id,
          });
        } catch (err) {
          console.warn("Failed to track event:", err);
        }
      }
    } catch (err) {
      setError("Failed to link bank account. Please try again.");
      console.error("Error exchanging token:", err);
    } finally {
      setLoading(false);
    }
  };

  const handlePlaidExit = (err, metadata) => {
    if (err) {
      setError("Bank account linking was cancelled or failed.");
      console.error("Plaid exit error:", err);
    }

    onExit();

    try {
      trackEvent("plaid_link_exit", {
        tenantId,
        error: err?.error_code,
        reason: metadata?.reason,
      });
    } catch (err) {
      console.warn("Failed to track event:", err);
    }
  };

  const { open, ready } = usePlaidLink({
    token: linkToken,
    onSuccess: handlePlaidSuccess,
    onExit: handlePlaidExit,
  });

  useEffect(() => {
    loadBankAccounts();
  }, [loadBankAccounts]);

  const handleLinkBankAccount = () => {
    if (ready) {
      open();
    } else {
      createLinkToken();
    }
  };

  const handleRemoveBankAccount = async (accountId) => {
    try {
      setLoading(true);

      // Note: This endpoint would need to be implemented on the backend
      // const response = await plaidApi.removeBankAccount(tenantId, accountId);

      message.success("Bank account removed successfully!");
      await loadBankAccounts();

      try {
        trackEvent("plaid_account_removed", { tenantId, accountId });
      } catch (err) {
        console.warn("Failed to track event:", err);
      }
    } catch (err) {
      setError("Failed to remove bank account. Please try again.");
      console.error("Error removing bank account:", err);
    } finally {
      setLoading(false);
    }
  };

  const renderBankAccountItem = (account) => (
    <BankAccountItem>
      <BankAccountInfo>
        <BankAccountAvatar>
          <BankOutlined />
        </BankAccountAvatar>
        <BankAccountDetails>
          <BankAccountName>{account.name}</BankAccountName>
          <BankAccountNumber>
            ****{account.mask} • {account.type}
          </BankAccountNumber>
        </BankAccountDetails>
      </BankAccountInfo>

      <BankAccountActions>
        <CheckCircleOutlined style={{ color: "#52c41a", marginRight: "8px" }} />
        <span style={{ color: "#52c41a", fontSize: "0.9rem" }}>Linked</span>
        <RemoveButton
          type="text"
          size="small"
          icon={<DeleteOutlined />}
          onClick={() => handleRemoveBankAccount(account.id)}
          danger
        >
          Remove
        </RemoveButton>
      </BankAccountActions>
    </BankAccountItem>
  );

  return (
    <PlaidLinkCard>
      <CardHeader>
        <CardTitle>Bank Account Linking</CardTitle>
        <CardDescription>
          Link your bank account to enable ACH payments for rent. Your bank
          information is securely encrypted and never stored on our servers.
        </CardDescription>
      </CardHeader>

      {error && (
        <Alert
          message="Error"
          description={error}
          type="error"
          showIcon
          closable
          onClose={() => setError(null)}
          style={{ marginBottom: "1rem" }}
        />
      )}

      {bankAccounts.length > 0 ? (
        <BankAccountsSection>
          <SectionTitle>Linked Bank Accounts</SectionTitle>
          <BankAccountsList
            dataSource={bankAccounts}
            renderItem={renderBankAccountItem}
            loading={loading}
          />
        </BankAccountsSection>
      ) : (
        <EmptyState>
          <EmptyStateIcon>
            <BankOutlined />
          </EmptyStateIcon>
          <EmptyStateTitle>No Bank Accounts Linked</EmptyStateTitle>
          <EmptyStateDescription>
            Link your bank account to enable ACH payments for rent.
          </EmptyStateDescription>
        </EmptyState>
      )}

      <ActionSection>
        <LinkButton
          type="primary"
          size="large"
          icon={<PlusOutlined />}
          onClick={handleLinkBankAccount}
          loading={loading}
        >
          {bankAccounts.length > 0
            ? "Add Another Bank Account"
            : "Link Bank Account"}
        </LinkButton>

        <SecurityNote>
          <strong>Security:</strong> Your bank credentials are encrypted and
          processed securely by Plaid. We never store your login information.
        </SecurityNote>
      </ActionSection>
    </PlaidLinkCard>
  );
};

// Styled Components
const PlaidLinkCard = styled(Card)`
  max-width: 600px;
  margin: 0 auto;
  border-radius: 16px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);

  .ant-card-body {
    padding: 2rem;
  }
`;

const CardHeader = styled.div`
  text-align: center;
  margin-bottom: 2rem;
`;

const CardTitle = styled.h2`
  color: var(--color-primary);
  font-size: 1.5rem;
  font-weight: 700;
  margin-bottom: 1rem;
  font-family: var(--font-primary);
`;

const CardDescription = styled.p`
  color: var(--color-text-light);
  font-size: 1rem;
  line-height: 1.6;
  margin: 0;
`;

const BankAccountsSection = styled.div`
  margin-bottom: 2rem;
`;

const SectionTitle = styled.h3`
  color: var(--color-text-primary);
  font-size: 1.2rem;
  font-weight: 600;
  margin-bottom: 1rem;
`;

const BankAccountsList = styled(List)`
  .ant-list-item {
    padding: 1rem;
    border: 1px solid var(--color-border-light);
    border-radius: 12px;
    margin-bottom: 0.5rem;

    &:hover {
      border-color: var(--color-primary);
      box-shadow: 0 2px 8px rgba(44, 95, 45, 0.1);
    }
  }
`;

const BankAccountItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
`;

const BankAccountInfo = styled.div`
  display: flex;
  align-items: center;
  flex: 1;
`;

const BankAccountAvatar = styled(Avatar)`
  background: var(--color-primary);
  margin-right: 1rem;

  .anticon {
    color: white;
  }
`;

const BankAccountDetails = styled.div`
  flex: 1;
`;

const BankAccountName = styled.div`
  font-weight: 600;
  color: var(--color-text-primary);
  font-size: 1rem;
  margin-bottom: 0.25rem;
`;

const BankAccountNumber = styled.div`
  color: var(--color-text-light);
  font-size: 0.9rem;
`;

const BankAccountActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const RemoveButton = styled(Button)`
  font-size: 0.8rem;
  height: 28px;
  padding: 0 0.5rem;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem 1rem;
`;

const EmptyStateIcon = styled.div`
  font-size: 3rem;
  color: var(--color-text-light);
  margin-bottom: 1rem;
`;

const EmptyStateTitle = styled.h3`
  color: var(--color-text-primary);
  font-size: 1.2rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
`;

const EmptyStateDescription = styled.p`
  color: var(--color-text-light);
  font-size: 1rem;
  margin: 0;
`;

const ActionSection = styled.div`
  text-align: center;
`;

const LinkButton = styled(Button)`
  height: 48px;
  font-size: 1.1rem;
  font-weight: 600;
  border-radius: 12px;
  padding: 0 2rem;
  margin-bottom: 1rem;
`;

const SecurityNote = styled.div`
  color: var(--color-text-light);
  font-size: 0.9rem;
  line-height: 1.5;
  max-width: 400px;
  margin: 0 auto;
`;

export default PlaidLink;
